import { ApiRoomContainer } from '@ibe/api';
import { range } from 'lodash-es';
import { action, computed, makeObservable, observable } from 'mobx';
import { ReactSelectOption } from '../../../../Models/ReactSelectOption';
import { TravellersAndCabin } from '../TravellerAndClassSelection/TravellersAndCabin';

class PaxRoomSelectionController {
  rooms: ApiRoomContainer[] = [
    { adults: 2, children: 0, infants: 0, childrenAges: [], infantsAges: [] }
  ];

  roomCount: ReactSelectOption | null = null;

  errors: { [key: string]: string[] };

  defaultPersonsAmount = 10;

  defaultMaxCapacityPerRoom = 10;

  defaultChildAgeLimit = 18;

  defaultMaxRooms = 10;

  private _defaultInitialOccupancy: ApiRoomContainer = {
    adults: 2,
    children: 0,
    infants: 0,
    childrenAges: [],
    infantsAges: []
  };

  private _initialOccupancy: ApiRoomContainer = {
    adults: 2,
    children: 0,
    infants: 0,
    childrenAges: [],
    infantsAges: []
  };

  _maxAdults = this.defaultPersonsAmount;

  _maxChildren = this.defaultPersonsAmount;

  _maxCapacity = this.defaultPersonsAmount;

  _maxCapacityPerRoom = this.defaultMaxCapacityPerRoom;

  _maxRooms = this.defaultMaxRooms;

  _minAdults = 1;

  personCountOptions: ReactSelectOption[];

  childAgeOptions: ReactSelectOption[] = range(0, this.defaultChildAgeLimit).map(val => {
    return { value: val.toString(), label: val.toString() };
  });

  roomCountOptions: ReactSelectOption[] = range(1, this._maxRooms).map((val: number) => {
    return { value: val.toString(), label: val.toString() };
  });

  constructor() {
    makeObservable(this, {
      rooms: observable,
      roomCount: observable,
      _maxAdults: observable,
      _minAdults: observable,
      _maxChildren: observable,
      _maxCapacity: observable,
      _maxCapacityPerRoom: observable,
      setRooms: action,
      setRoomCount: action,
      setRoomCountAndTriggerRoomCreation: action,
      triggerRoomCreation: action,
      triggerRemoveRoom: action,
      setAdultForRoom: action,
      setChildrenForRoom: action,
      setInfantForRoom: action,
      travelersAmount: computed,
      allTravelersAmount: computed,
      roomAmount: computed,
      getTravellers: action,
      isAgeSetForAllTravellers: computed,
      setMaxAdults: action,
      setMaxChildren: action,
      setMaxCapacity: action,
      setMaxCapacityPerRoom: action,
      setMinAdults: action
    });
  }

  setRooms(value: ApiRoomContainer[]): void {
    this.rooms = value;
  }

  setRoomCount(value: number): void {
    this.roomCount = this.getRoomCountOption(value);
  }

  setInitialOccupancy(value: ApiRoomContainer) {
    this._initialOccupancy = value;
  }

  setRoomCountAndTriggerRoomCreation(value: ReactSelectOption | null): void {
    this.roomCount = value as ReactSelectOption;
    this.triggerRoomCreation();
  }

  triggerRoomCreation(): void {
    let count = 0;
    if (this.roomCount) {
      count = +(this.roomCount as ReactSelectOption).value;
    }
    // TODO workaround, dont know why
    this.rooms = !this.rooms ? [] : this.rooms;

    if (this.rooms && this.rooms.length < count) {
      let i = this.rooms.length;

      let personCount = this._maxCapacity - this.getAdultCount() - this.getChildrenCount();
      for (i; i < count; i += 1) {
        if (personCount > 2) {
          this.rooms.push(this._defaultInitialOccupancy);

          personCount -=
            this._defaultInitialOccupancy.adults +
            this._defaultInitialOccupancy.children +
            this._defaultInitialOccupancy.infants;
        } else {
          this.rooms.push({
            adults: personCount - 1,
            children: 0,
            infants: 0,
            childrenAges: [],
            infantsAges: []
          });
          personCount = 0;
        }
      }
    }
    if (this.rooms && this.rooms.length > count) {
      let j = this.rooms.length;
      for (j; j > count; j -= 1) {
        this.rooms.pop();
      }
    }
  }

  createRoom(): void {
    const availableSpaceLeft =
      this._maxCapacity - this.getAdultCount() - this.getChildrenCount() - this.getInfantCount();

    const defaultOccupancyCount =
      this._defaultInitialOccupancy.adults +
      this._defaultInitialOccupancy.children +
      this._defaultInitialOccupancy.infants;

    const initialOccupancyCount =
      this._initialOccupancy.adults +
      this._initialOccupancy.children +
      this._initialOccupancy.infants;

    const occupancyForNewRoom = initialOccupancyCount
      ? initialOccupancyCount
      : defaultOccupancyCount;

    if (availableSpaceLeft <= 0) return;

    if (availableSpaceLeft >= occupancyForNewRoom) {
      this.rooms.push(this._initialOccupancy || this._defaultInitialOccupancy);
    } else {
      this.rooms.push({
        adults: availableSpaceLeft,
        children: 0,
        infants: 0,
        childrenAges: [],
        infantsAges: []
      });
    }
    this.roomCount = this.getRoomCountOption(this.rooms.length);
  }

  triggerRemoveRoom(id: number): void {
    this.rooms.splice(id, 1);
    const currentRoomCount = this.roomCount;
    if (currentRoomCount?.value) {
      this.setRoomCount(parseInt(currentRoomCount.value) - 1);
    }
  }

  setMaxCapacity(value: number | undefined) {
    if (value) {
      this._maxCapacity = value + 1;
    }
  }

  setMaxCapacityPerRoom(value: number | undefined) {
    if (value) {
      this._maxCapacityPerRoom = value;
    }
  }

  setMaxRooms(value: number | undefined) {
    this._maxRooms = value ? value + 1 : this.defaultMaxRooms;
    this.roomCountOptions = range(1, this._maxRooms).map((val: number) => {
      return { value: val.toString(), label: val.toString() };
    });
  }

  get maxRooms(): number {
    return this._maxRooms;
  }

  getPersonCountOptions() {
    return range(0, this._maxCapacity).map(val => {
      return { value: val.toString(), label: val.toString() };
    });
  }

  getRoomCountOption(value: number): ReactSelectOption {
    const defaultValue = { value: '1', label: '1' };
    return this.roomCountOptions.find(item => item.value === value.toString()) || defaultValue;
  }

  getSelectedChildAgeOption = (value: number): ReactSelectOption | null => {
    if (!value && value !== 0) {
      // could be also null, NaN, empty string or undefined
      return null;
    }
    return { value: value.toString(), label: value.toString() };
  };

  getPersonCountOption(value: number): ReactSelectOption {
    const defaultValue = { value: '1', label: '1' };
    return (
      this.getPersonCountOptions().find(item => item.value === value.toString()) || defaultValue
    );
  }

  getChildAgeOption(value: number): ReactSelectOption {
    const defaultValue = { value: '1', label: '1' };
    return this.childAgeOptions.find(item => item.value === value.toString()) || defaultValue;
  }

  setAdultForRoom(roomIndex: number, value: ReactSelectOption, callback?: () => void): void {
    if (this.rooms && this.rooms[roomIndex] && value) {
      const reactSelectOptionValue = (value as ReactSelectOption).value;
      this.rooms = this.rooms.map((room, index) =>
        index === roomIndex ? { ...room, adults: +reactSelectOptionValue } : room
      );
      if (callback) {
        callback();
      }
    }
  }

  setChildrenForRoom(roomIndex: number, value: ReactSelectOption): void {
    if (this.rooms && this.rooms[roomIndex] && value) {
      const reactSelectOptionValue = +(value as ReactSelectOption).value;
      this.rooms = this.rooms.map((room, index) => {
        if (index === roomIndex) {
          const updatedChildrenAges =
            room.childrenAges.length > reactSelectOptionValue
              ? room.childrenAges.slice(0, reactSelectOptionValue)
              : room.childrenAges;

          return {
            ...room,
            children: reactSelectOptionValue,
            childrenAges: updatedChildrenAges
          };
        }
        return room;
      });
    }
  }

  setInfantForRoom(roomIndex: number, value: ReactSelectOption): void {
    if (this.rooms && this.rooms[roomIndex] && value) {
      const reactSelectOptionValue = Number(value.value);
      if (!isNaN(reactSelectOptionValue)) {
        this.rooms = this.rooms.map((room, index) => {
          if (index === roomIndex) {
            const updatedInfantAges =
              room.infantsAges.length > reactSelectOptionValue
                ? room.infantsAges.slice(0, reactSelectOptionValue)
                : room.infantsAges;

            return {
              ...room,
              infants: reactSelectOptionValue,
              infantsAges: updatedInfantAges
            };
          }
          return room;
        });
      }
    }
  }

  setChildAgeForRoom(roomIndex: number, childIndex: number, value: ReactSelectOption | null): void {
    if (!this.rooms || !this.rooms[roomIndex]) {
      return;
    }

    this.rooms = this.rooms.map((room, index) => {
      if (index === roomIndex) {
        const updatedChildrenAges = [...room.childrenAges];
        for (let i = updatedChildrenAges.length; i <= childIndex; i += 1) {
          updatedChildrenAges[i] = NaN;
        }
        if (value) {
          updatedChildrenAges[childIndex] = Number((value as ReactSelectOption).value);
        }
        return {
          ...room,
          childrenAges: updatedChildrenAges
        };
      }
      return room;
    });
  }

  get travelersAmount(): number {
    let travelers = 0;
    if (this.rooms && this.rooms.length > 0) {
      this.rooms.forEach(pax => {
        travelers += pax.adults + pax.children;
      });
    }
    return travelers;
  }

  get allTravelersAmount(): number {
    let travelers = 0;
    if (this.rooms && this.rooms.length > 0) {
      this.rooms.forEach(pax => {
        travelers += pax.adults + pax.children + pax.infants;
      });
    }
    return travelers;
  }

  get roomAmount(): number {
    if (this.rooms && this.rooms.length > 0) {
      return this.rooms.length;
    }
    return 0;
  }

  get numberOfAdults(): number {
    let adults = 0;
    if (this.rooms && this.rooms.length > 0) {
      this.rooms.forEach(pax => {
        adults += pax.adults;
      });
    }
    return adults;
  }

  get numberOfChildren(): number {
    let children = 0;
    if (this.rooms && this.rooms.length > 0) {
      this.rooms.forEach(pax => {
        children += pax.children;
      });
    }
    return children;
  }

  validateProvidedAges(): boolean {
    let isValid = true;
    this.rooms.forEach(room => {
      if (room.children !== room.childrenAges.length) {
        isValid = false;
      }
      const validNumbers = !room.childrenAges.some(Number.isNaN);
      if (!validNumbers) {
        isValid = validNumbers;
      }
    });

    return isValid;
  }

  areChildrenOverXYears(minChildAgeToGoInTour: number): boolean {
    let areChildrenOverXYears = true;
    this.rooms.forEach(r => {
      r.childrenAges.forEach(childAge => {
        if (childAge < minChildAgeToGoInTour) {
          areChildrenOverXYears = false;
        }
      });
    });
    return areChildrenOverXYears;
  }

  getTravellers(): Omit<TravellersAndCabin, 'cabinClass'> {
    return {
      adults: this.rooms?.length > 0 ? this.rooms[0].adults : 0,
      children: this.rooms?.length > 0 ? this.rooms[0].children : 0,
      childrenAges: this.rooms?.length > 0 ? this.rooms[0].childrenAges : [],
      infants: this.rooms?.length > 1 ? this.rooms[1].children : 0,
      infantAges: this.rooms?.length > 1 ? this.rooms[1].childrenAges : []
    };
  }

  get isAgeSetForAllTravellers(): boolean {
    return !this.rooms[0].childrenAges.includes(NaN) && !this.rooms[1].childrenAges.includes(NaN);
  }

  setMaxAdults(value: number): void {
    this._maxAdults = value;
  }

  setMaxChildren(value: number): void {
    this._maxChildren = value;
  }

  get initialMaxAdults(): number {
    return this._maxAdults;
  }

  get initialMaxChildren(): number {
    return this._maxChildren;
  }

  get getMaxCapacity(): number {
    return this._maxCapacity;
  }

  getAdultCount(ignoreIndex = -1): number {
    let countAdults = 0;
    this.rooms.forEach((room, index) => {
      if (ignoreIndex !== index) countAdults += room.adults;
    });
    return countAdults;
  }

  getAdultCountInRoom(roomIndex: number): number {
    let countAdults = 0;
    this.rooms.forEach((room, index) => {
      if (index === roomIndex) countAdults += room.adults;
    });
    return countAdults;
  }

  getChildrenCount(ignoreIndex = -1): number {
    let countChildren = 0;
    this.rooms.forEach((room, index) => {
      if (index !== ignoreIndex) countChildren += room.children;
    });
    return countChildren;
  }

  getChildrenCountInRoom(roomIndex: number): number {
    let countChildren = 0;
    this.rooms.forEach((room, index) => {
      if (index === roomIndex) countChildren += room.children;
    });
    return countChildren;
  }

  getInfantCount(ignoreIndex = -1): number {
    let countInfants = 0;
    this.rooms.forEach((room, index) => {
      if (index !== ignoreIndex) countInfants += room.infants;
    });
    return countInfants;
  }

  getInfantCountInRoom(roomIndex: number): number {
    let countInfants = 0;
    this.rooms.forEach((room, index) => {
      if (index === roomIndex) countInfants += room.infants;
    });
    return countInfants;
  }

  getMaxAdultsLeftPerRoom(roomIndex: number): number {
    if (this.allTravelersAmount >= this._maxCapacity) {
      return 0;
    } else {
      return (
        this._maxCapacity -
        this._maxCapacityPerRoom -
        this.getChildrenCountInRoom(roomIndex) -
        this.getInfantCountInRoom(roomIndex)
      );
    }
  }

  getMaxChildrenLeftPerRoom(roomIndex: number): number {
    if (this.allTravelersAmount >= this._maxCapacity) {
      return 0;
    } else {
      return (
        this._maxCapacity -
        this._maxCapacityPerRoom -
        this.getAdultCountInRoom(roomIndex) -
        this.getInfantCountInRoom(roomIndex)
      );
    }
  }

  getMaxInfantsLeftPerRoom(roomIndex: number): number {
    if (this.allTravelersAmount >= this._maxCapacity) {
      return 0;
    } else {
      return (
        this._maxCapacity -
        this._maxCapacityPerRoom -
        this.getAdultCountInRoom(roomIndex) -
        this.getChildrenCountInRoom(roomIndex)
      );
    }
  }

  getMaxAdults(index: number): number {
    const amount = this._maxCapacity - this.getAdultCount(index) - this.getChildrenCount();
    return amount > this._maxAdults ? this._maxAdults : amount;
  }

  getMaxChildren(index: number): number {
    const amount = this._maxCapacity - this.getAdultCount() - this.getChildrenCount(index);
    return amount > this._maxChildren ? this._maxChildren : amount;
  }

  setMinAdults(value: number): void {
    this._minAdults = value;
  }

  getMinAdults(): number {
    return this._minAdults;
  }
}

export default PaxRoomSelectionController;
